import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import Flatpickr from "react-flatpickr";
import Popup from "reactjs-popup";
import { offsetDateForServer } from "../../../shared/timeZoneLogic";
import { useSeriesShowContext } from "../SeriesShowApp";
import { Button, TileGroup } from "@opendate-app/setlist";
import useCheckoutQuestions from "../useCheckoutQuestions";
import CheckoutQuestionsEditor from "./CheckoutQuestionsEditor";
import PassFeesSection from "./PassFeesSection";
import InventoryPoolSelect from "./InventoryPoolSelect";
import GeneralLedgerCodeSelect from "./GeneralLedgerCodeSelect";

// Display format for the delivery picker, matching the other SeriesShow pickers.
const FLATPICKR_FORMAT = "n/j/y at h:iK";

// Flatpickr hands back a Date; the API (and the tests) expect a local
// "YYYY-MM-DDTHH:mm" string, so convert on the way out.
const toLocalDatetimeString = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const POPUP_STYLE = {
  border: "none",
  borderRadius: 8,
  padding: 0,
  width: "min(440px, calc(100vw - 40px))",
  textAlign: "left",
};

// A blocked Season Pass stays on screen, disabled, with the series' reason in
// place of its description, so the organizer sees why only a Flex Pack is offered.
const kindTiles = (seasonPassBlockedReason) => [
  {
    value: "season_pass",
    label: "Season Pass",
    supportingText: seasonPassBlockedReason || "Every date, one barcode.",
    disabled: Boolean(seasonPassBlockedReason),
  },
  {
    value: "flex_pack",
    label: "Flex Pack",
    supportingText: "Any number of dates you choose. Fans pick them later.",
  },
];

const CreatePassProductModal = ({ open, onClose }) => {
  const { handleCreatePassProduct, series } = useSeriesShowContext();
  const seasonPassBlockedReason = series.season_pass_blocked_reason || null;

  // The venue's taxes the organizer can apply, supplied by the series blueprint.
  const availableTaxes = useMemo(() => series.available_manual_taxes || [], [series]);

  const [productKind, setProductKind] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [packSize, setPackSize] = useState("");
  const [price, setPrice] = useState("");
  const [capacity, setCapacity] = useState("");
  const [maxPerOrder, setMaxPerOrder] = useState("");
  const [inventoryPoolId, setInventoryPoolId] = useState("");
  const [generalLedgerCodeId, setGeneralLedgerCodeId] = useState("");
  const [selectedTaxIds, setSelectedTaxIds] = useState([]);
  const [roundUp, setRoundUp] = useState(false);
  const [deliverLater, setDeliverLater] = useState(false);
  const [deliveryTime, setDeliveryTime] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const questions = useCheckoutQuestions();

  useEffect(() => {
    if (!open) return;
    questions.reset();
    setProductKind(seasonPassBlockedReason ? "flex_pack" : "season_pass");
    setName("");
    setDescription("");
    setPackSize("");
    setPrice("");
    setCapacity("");
    setMaxPerOrder("");
    setInventoryPoolId("");
    setGeneralLedgerCodeId("");
    // Default to all applicable taxes applied — the same default the ticket-type
    // editor uses. The organizer unchecks any that don't apply.
    setSelectedTaxIds(availableTaxes.map((tax) => tax.id));
    setRoundUp(false);
    // Deliver immediately unless the organizer opts into holding it back.
    setDeliverLater(false);
    setDeliveryTime("");
    setError(null);
    setSubmitting(false);
  }, [open]);

  const toggleTax = (taxId) =>
    setSelectedTaxIds((current) =>
      current.includes(taxId) ? current.filter((id) => id !== taxId) : [...current, taxId],
    );

  const isFlex = productKind === "flex_pack";
  // Blank means "no limit"; a filled value must be a positive whole number. The
  // submit is a plain button (no <form>), so the input's min="1" enforces
  // nothing — validate here rather than bounce off a generic server 422.
  const maxPerOrderValid =
    String(maxPerOrder).trim() === "" ||
    (Number.isInteger(Number(maxPerOrder)) && Number(maxPerOrder) > 0);
  const isValid =
    productKind &&
    !(productKind === "season_pass" && seasonPassBlockedReason) &&
    name.trim() &&
    Number(price) > 0 &&
    Number(capacity) > 0 &&
    (!isFlex || Number(packSize) > 0) &&
    (!deliverLater || deliveryTime !== "") &&
    maxPerOrderValid &&
    questions.valid;

  const submit = async () => {
    if (submitting || !isValid) return;
    try {
      setSubmitting(true);
      setError(null);
      await handleCreatePassProduct({
        product_kind: productKind,
        name: name.trim(),
        description: description.trim() || null,
        price,
        capacity,
        pack_size: isFlex ? packSize : null,
        delivery_type: deliverLater ? "delayed" : "immediate",
        // Re-interpret the picked wall-clock in the VENUE's zone (mirrors
        // AddShowModal) so the server stores an absolute instant instead of
        // casting the naive string in the organizer's profile zone.
        delivery_time: deliverLater ? offsetDateForServer(deliveryTime, series.venue_time_zone) : null,
        max_per_order: String(maxPerOrder).trim() === "" ? null : Number(maxPerOrder),
        round_up: roundUp,
        series_pass_inventory_pool_id: inventoryPoolId || null,
        ...(series.available_general_ledger_codes ? { general_ledger_code_id: generalLedgerCodeId || null } : {}),
        taxable_items_attributes: selectedTaxIds.map((id) => ({ manual_tax_id: id })),
        custom_fields_attributes: questions.attributes(),
      });
      onClose();
    } catch (e) {
      const apiErrors = e && e.response && e.response.data && e.response.data.errors;
      setError(
        Array.isArray(apiErrors)
          ? apiErrors.join(", ")
          : apiErrors || "Could not create this product. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Popup open={open} modal className="popup-modal" contentStyle={POPUP_STYLE} onClose={onClose} closeOnDocumentClick>
      <div className="popup-modal-container">
        <div className="row m-0 series-confirm-header">
          <div className="col series-confirm-header-col">
            <p className="mb-0 series-confirm-title"><strong>Create a pass or pack</strong></p>
          </div>
          <div className="col-xs-auto d-flex align-items-center">
            <a href="#" className="text-muted series-confirm-close" onClick={(e) => { e.preventDefault(); onClose(); }}>
              <i className="fa fa-times" />
            </a>
          </div>
        </div>
        <div className="row m-0">
          <div className="col-12 series-confirm-body">
            <div className="mb-3" data-testid="pass-product-kind">
              <TileGroup
                type="radio"
                columns={2}
                name="pass-product-kind"
                aria-label="Kind"
                value={productKind}
                items={kindTiles(seasonPassBlockedReason)}
                onChange={setProductKind}
              />
            </div>

            <div className="mb-3">
              <label className="form-label"><strong>Name</strong></label>
              <input
                type="text"
                className="form-control"
                data-testid="pass-product-name"
                placeholder="e.g. Season Pass"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="mb-3">
              <label className="form-label"><strong>Description</strong></label>
              <textarea
                className="form-control"
                data-testid="pass-product-description"
                rows={4}
                placeholder={"Shown to fans before they buy — what this pass gets them.\nEach new line shows as its own line, so you can list what's included."}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="mb-3">
              <label className="form-label"><strong>Price</strong></label>
              <div className="input-group">
                <span className="input-group-text">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="form-control"
                  data-testid="pass-product-price"
                  placeholder="0.00"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                />
              </div>
            </div>

            {isFlex && (
              <div className="mb-3">
                <label className="form-label"><strong>Number of tickets</strong></label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  className="form-control"
                  data-testid="pass-product-pack-size"
                  placeholder="e.g. 10"
                  value={packSize}
                  onChange={(e) => setPackSize(e.target.value)}
                />
                <small className="text-muted">How many shows this pack lets a fan into.</small>
              </div>
            )}

            {availableTaxes.length > 0 && (
              <div className="mb-3" data-testid="pass-product-taxes">
                <label className="form-label"><strong>Taxes</strong></label>
                {availableTaxes.map((tax) => (
                  <div className="form-check" key={tax.id}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={`pass-tax-${tax.id}`}
                      data-testid={`pass-product-tax-${tax.id}`}
                      checked={selectedTaxIds.includes(tax.id)}
                      onChange={() => toggleTax(tax.id)}
                    />
                    <label className="form-check-label" htmlFor={`pass-tax-${tax.id}`}>
                      {tax.name} ({parseFloat(tax.rate)}%)
                    </label>
                  </div>
                ))}
                <small className="text-muted">Applied on top of the price at checkout.</small>
              </div>
            )}

            <label className="form-label"><strong>Fees</strong></label>
            <PassFeesSection
              idPrefix="pass-product"
              enabled={open}
              price={price}
              roundUp={roundUp}
              onRoundUpChange={setRoundUp}
              taxIds={selectedTaxIds}
            />

            <div className="mb-3" data-testid="pass-product-delivery">
              <label className="form-label"><strong>Delivery</strong></label>
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="pass-deliver-later"
                  data-testid="pass-product-deliver-later"
                  checked={deliverLater}
                  onChange={() => setDeliverLater((current) => !current)}
                />
                <label className="form-check-label" htmlFor="pass-deliver-later">
                  {isFlex ? "Hold the redemption code until a set date" : "Hold the barcode until a set date"}
                </label>
              </div>
              {deliverLater && (
                <div className="mt-2">
                  <Flatpickr
                    className="form-control"
                    data-testid="pass-product-delivery-time"
                    aria-label="Delivery date"
                    placeholder="Select date / time"
                    options={{ enableTime: true, dateFormat: FLATPICKR_FORMAT, minDate: "today" }}
                    value={deliveryTime ? new Date(deliveryTime) : ""}
                    onClose={([date]) => setDeliveryTime(toLocalDatetimeString(date))}
                  />
                  <small className="text-muted" data-testid="pass-product-delivery-help">
                    {isFlex
                      ? "Buyers see “available on this date” instead of their redemption code until then. A signed-in buyer's pack still applies at checkout, so they can claim shows early."
                      : "Buyers see “available on this date” until then. The pass still scans at the door, so you can admit someone early."}
                  </small>
                </div>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label"><strong>Capacity</strong></label>
              <input
                type="number"
                min="1"
                step="1"
                className="form-control"
                data-testid="pass-product-capacity"
                placeholder="How many can be sold"
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
              />
              <small className="text-muted">Should not exceed the smallest show's inventory.</small>
            </div>

            <InventoryPoolSelect
              pools={series.series_pass_inventory_pools || []}
              value={inventoryPoolId}
              onChange={setInventoryPoolId}
            />

            <GeneralLedgerCodeSelect
              codes={series.available_general_ledger_codes}
              value={generalLedgerCodeId}
              onChange={setGeneralLedgerCodeId}
            />

            <div className="mb-3">
              <label className="form-label"><strong>Limit per order</strong></label>
              <input
                type="number"
                min="1"
                step="1"
                className="form-control"
                data-testid="pass-product-max-per-order"
                placeholder="No limit"
                value={maxPerOrder}
                onChange={(e) => setMaxPerOrder(e.target.value)}
              />
              <small className="text-muted">Most a single buyer can purchase in one order. Leave blank for no limit.</small>
            </div>

            <label className="form-label"><strong>Checkout questions</strong></label>
            <CheckoutQuestionsEditor questions={questions} />

            {error && (
              <div className="alert alert-danger py-2" data-testid="pass-product-error">{error}</div>
            )}

            <div className="d-flex justify-content-end series-confirm-actions">
              <Button variant="secondary" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button
                variant="primary"
                data-testid="pass-product-submit"
                onClick={submit}
                disabled={submitting || !isValid}
              >
                {submitting ? "Creating…" : "Create product"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Popup>
  );
};

CreatePassProductModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default CreatePassProductModal;
